import { BranchCode, RealEstateEligibilityDtoStatusEnum } from "api/risk"
import { useEffect, useState } from "react"
import { risksAPI } from "services/swagger-api/riskApi"
import { EligibilityVerdict } from "./useEligibilityCheck"

interface UseParcEligibilityOptions {
  /** GLI : clause résolutoire globale (« Oui pour tous »), transmise au back. */
  terminationClauseGlobal?: boolean
  /** GLI : surcharge de la clause par bail (prioritaire sur le défaut global). */
  terminationClauseOverrides?: Record<string, boolean>
  /** Incrémenter pour relancer l'évaluation. */
  refreshKey?: number
  enabled?: boolean
}

/**
 * Évalue l'éligibilité de tous les biens d'un parc en un seul appel batch
 * (le back accepte un tableau de biens). Retourne un verdict par bien.
 */
export const useParcEligibility = (
  branchCode: BranchCode,
  realEstateIds: string[],
  { terminationClauseGlobal, terminationClauseOverrides, refreshKey, enabled = true }: UseParcEligibilityOptions = {}
) => {
  const [verdicts, setVerdicts] = useState<Record<string, EligibilityVerdict>>({})
  const [isLoading, setIsLoading] = useState(false)

  const idsKey = realEstateIds.join(",")
  const overridesKey = JSON.stringify(terminationClauseOverrides ?? {})

  useEffect(() => {
    if (!enabled || realEstateIds.length === 0) {
      setVerdicts({})
      return
    }

    let cancelled = false

    const evaluate = async () => {
      setIsLoading(true)
      try {
        const response = await risksAPI.evaluateEligibility({
          evaluateEligibilityCommandDto: {
            branchCode,
            realEstateIds,
            terminationClauseGlobal,
            terminationClauseOverrides
          }
        })

        if (cancelled) return

        const map: Record<string, EligibilityVerdict> = {}
        for (const result of response.realEstates ?? []) {
          if (!result.realEstateId) continue
          map[result.realEstateId] = {
            status: result.status as RealEstateEligibilityDtoStatusEnum,
            reasons: result.reasons ?? [],
            isEligible: result.status === RealEstateEligibilityDtoStatusEnum.Eligible
          }
        }
        setVerdicts(map)
      } catch (error) {
        console.error("Erreur lors de l'évaluation d'éligibilité du parc:", error)
        if (!cancelled) setVerdicts({})
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    evaluate()

    return () => {
      cancelled = true
    }
    // realEstateIds / terminationClauseOverrides stabilisés via idsKey / overridesKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchCode, idsKey, terminationClauseGlobal, overridesKey, refreshKey, enabled])

  return { verdicts, isLoading }
}
